/**
 * Structural operations (SPEC §6.1): pure functions `(ctx, selection, input) → { ops, caret }`.
 * They never mutate the graph; the caller applies `ops` as one transaction (one undo unit).
 * The same functions run on the client and the server.
 *
 * Semantics follow Logseq 0.10.9 (file-based) where it defines them, with these deliberate
 * differences (DECISIONS.md D29–D33; the alternatives are settings, see settings.ts):
 * - Enter on an empty top-level block does nothing (SPEC §6.3; Logseq inserts a block).
 * - ⌫ merge with children never refuses: children keep their depth under the previous sibling
 *   or take the merged block's slot (Logseq refuses when both blocks have children).
 * - Multi-level selections indent/outdent by exactly one level per root, all or nothing.
 * - Moving a node into a collapsed target expands the target, so nothing disappears.
 */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { makeNode, type Node } from './model.ts'
import { op, type OpBody } from './ops.ts'
import { keysBetween } from './order.ts'
import type { Rand } from './rng.ts'
import { selectionGroups } from './selection.ts'
import { editorOptions, type EditorOptions } from './settings.ts'
import {
  hasLiveChildren,
  isAncestor,
  isFolded,
  keysAfter,
  keysAtEnd,
  keysAtStart,
  keysBefore,
  lastVisibleDescendant,
  liveChildren,
  liveSiblingsAfter,
  nextLiveSibling,
  nextVisible,
  prevLiveSibling,
  prevVisible,
} from './tree.ts'

export interface Ctx {
  readonly g: Graph
  /** View root: the page, journal day or zoomed block the user is looking at. */
  readonly root: NodeId
  readonly newId: () => NodeId
  readonly rand: Rand
  readonly now: number
  /** Settings → Editor; omitted options use the defaults (settings.ts). */
  readonly options?: Partial<EditorOptions>
}

export interface Caret {
  readonly id: NodeId
  readonly start: number
  readonly end: number
}

export interface Result {
  readonly ops: OpBody[]
  /** undefined = caret unchanged; null = no caret. */
  readonly caret?: Caret | null
  /** New block selection, for ops that select their result. */
  readonly select?: NodeId[]
  /** New view root (zoom). */
  readonly root?: NodeId
}

/** The editing block's live state: textarea value and selection, possibly ahead of the store. */
export interface TextInput {
  readonly id: NodeId
  readonly text: string
  readonly start: number
  readonly end: number
}

export const NOOP: Result = Object.freeze({ ops: [] }) as Result

const at = (id: NodeId, start: number, end = start): Caret => ({ id, start, end })

function newBlock(ctx: Ctx, parentId: NodeId, order: string, content: string, from?: Node): Node {
  return makeNode({
    id: ctx.newId(),
    kind: 'block',
    content,
    parentId,
    order,
    collapsed: from?.collapsed ?? false,
    tags: from?.tags ?? [],
    props: from?.props ?? {},
    origin: 'user',
    createdAt: ctx.now,
    updatedAt: ctx.now,
  })
}

/** Moving nodes into a target with the collapsed flag would hide them; expand it in the same batch. */
function expandIfCollapsed(g: Graph, id: NodeId, ops: OpBody[]): void {
  if (g.must(id).collapsed) ops.push(op.collapse(id, false))
}

function isBlockInView(ctx: Ctx, id: NodeId): boolean {
  const { g, root } = ctx
  return id !== root && g.isLive(id) && g.must(id).kind === 'block' && isAncestor(g, root, id)
}

// ------------------------------------------------------------------ Enter

/**
 * ⏎. Split at the caret (a text selection is replaced).
 * - In the zoom header: the rest becomes the first child.
 * - Empty top-level block: nothing (SPEC §6.3).
 * - Empty last child: outdent (Logseq). Empty block with siblings below: a new empty block below.
 * - Caret at the (whitespace-only) start of a non-empty block: empty block above, caret in it (Logseq).
 * - Otherwise the block keeps the text before the caret; the new block gets the text after it,
 *   as the first child if the block has expanded children, else as the next sibling.
 */
export function split(ctx: Ctx, input: TextInput): Result {
  const { g, root, rand } = ctx
  if (!g.isLive(input.id)) return NOOP
  const b = g.must(input.id)
  const text = input.text
  const start = Math.max(0, Math.min(input.start, text.length))
  const end = Math.max(start, Math.min(input.end, text.length))
  const before = text.slice(0, start)
  const after = text.slice(end).replace(/^\s+/, '')
  const ops: OpBody[] = []
  const setContent = (id: NodeId, c: string): void => {
    if (g.must(id).content !== c) ops.push(op.content(id, c))
  }

  if (b.id === root) {
    if (b.kind !== 'block') return NOOP
    setContent(b.id, before)
    const n = newBlock(ctx, b.id, keysAtStart(g, b.id, 1, rand)[0]!, after)
    ops.push(op.create(n))
    return { ops, caret: at(n.id, 0) }
  }
  if (!isBlockInView(ctx, b.id)) return NOOP
  const parentId = b.parentId!

  if (text.trim() === '') {
    if (parentId === root) return NOOP
    const always = editorOptions(ctx.options).emptyChildEnter === 'always-outdent'
    if (always || nextLiveSibling(g, b.id) === null) {
      const r = outdent(ctx, [b.id])
      if (r.ops.length > 0) {
        setContent(b.id, text)
        return { ops: [...ops, ...r.ops], caret: at(b.id, start, end) }
      }
    }
  } else if (before.trim() === '' && after !== '') {
    if (start !== end) setContent(b.id, text.slice(end))
    else setContent(b.id, text)
    const n = newBlock(ctx, parentId, keysBefore(g, parentId, b.id, 1, rand)[0]!, '')
    ops.push(op.create(n))
    return { ops, caret: at(n.id, 0) }
  }

  setContent(b.id, before)
  const n =
    !b.collapsed && hasLiveChildren(g, b.id)
      ? newBlock(ctx, b.id, keysAtStart(g, b.id, 1, rand)[0]!, after)
      : newBlock(ctx, parentId, keysAfter(g, parentId, b.id, 1, rand)[0]!, after)
  ops.push(op.create(n))
  return { ops, caret: at(n.id, 0) }
}

// ------------------------------------------------------------------ ⌫ at start

/**
 * ⌫ with the caret at offset 0 and no text selection. Joins the block's text onto the visually
 * previous block P (never across the view boundary) and deletes the block. Its children:
 * - previous sibling S exists and is not folded → appended to S's children (depth kept);
 * - otherwise (first child, or S folded) → they take the block's slot (one level up).
 * Either way they stay directly below the joined line. At the top of the view, an empty childless
 * block is deleted if the view has other blocks; anything else is a no-op.
 */
export function merge(ctx: Ctx, input: { id: NodeId; text: string }): Result {
  const { g, root, rand } = ctx
  if (!isBlockInView(ctx, input.id)) return NOOP
  const b = g.must(input.id)
  const parentId = b.parentId!
  const kids = liveChildren(g, b.id)
  const s = prevLiveSibling(g, b.id)
  const p = s !== null ? lastVisibleDescendant(g, s) : parentId !== root ? parentId : null

  if (p === null) {
    if (input.text.trim() !== '' || kids.length > 0) return NOOP
    if (liveChildren(g, root).length < 2) return NOOP
    const next = nextVisible(g, root, b.id)
    return { ops: [op.delete(b.id)], caret: next === null ? null : at(next, 0) }
  }

  // Logseq refuses when the block and the node to its left (previous sibling, or the parent for
  // a first child) both have children.
  if (kids.length > 0 && editorOptions(ctx.options).mergeWithChildren === 'refuse') {
    const left = s ?? (parentId !== root ? parentId : null)
    if (left !== null && hasLiveChildren(g, left)) return NOOP
  }

  const ops: OpBody[] = []
  const pNode = g.must(p)
  const joined = pNode.content + input.text
  if (joined !== pNode.content) ops.push(op.content(p, joined))
  if (kids.length > 0) {
    if (s !== null && !isFolded(g, s)) {
      expandIfCollapsed(g, s, ops)
      const keys = keysAtEnd(g, s, kids.length, rand)
      kids.forEach((k, i) => ops.push(op.move(k, s, keys[i]!)))
    } else {
      const keys = keysAfter(g, parentId, b.id, kids.length, rand)
      kids.forEach((k, i) => ops.push(op.move(k, parentId, keys[i]!)))
    }
  }
  ops.push(op.delete(b.id))
  return { ops, caret: at(p, pNode.content.length) }
}

// ------------------------------------------------------------------ Tab / ⇧Tab

/**
 * Tab. Every selected root goes exactly one level deeper, document order unchanged: each run of
 * sibling roots becomes the last children of the sibling just above the run. All or nothing:
 * if the first run has no sibling above it, nothing moves (a first child cannot indent).
 */
export function indent(ctx: Ctx, ids: readonly NodeId[]): Result {
  const { g, rand } = ctx
  const { groups } = selectionGroups(g, ctx.root, ids)
  if (groups.length === 0) return NOOP
  const ops: OpBody[] = []
  for (const run of groups) {
    const target = prevLiveSibling(g, run[0]!)
    if (target === null) return NOOP
    expandIfCollapsed(g, target, ops)
    const keys = keysAtEnd(g, target, run.length, rand)
    run.forEach((id, i) => ops.push(op.move(id, target, keys[i]!)))
  }
  return { ops }
}

/**
 * ⇧Tab. Every selected root goes one level up, placed right after its old parent. Direct
 * outdenting (default, Logseq's default): unselected siblings below the last root become its
 * children, so nothing on screen changes order or depth except the outdented blocks. Logical
 * outdenting (option): those siblings stay with the old parent, so the block lands below them.
 * All or nothing: if any root is already top-level in the view, nothing moves.
 */
export function outdent(ctx: Ctx, ids: readonly NodeId[]): Result {
  const { g, root, rand } = ctx
  const { groups } = selectionGroups(g, root, ids)
  if (groups.length === 0) return NOOP
  for (const run of groups) {
    const parentId = g.must(run[0]!).parentId
    if (parentId === null || parentId === root || g.must(parentId).parentId === null) return NOOP
  }
  const ops: OpBody[] = []
  const lastRun = groups[groups.length - 1]!
  const last = lastRun[lastRun.length - 1]!
  const logical = editorOptions(ctx.options).outdent === 'logical'
  const right = logical ? [] : liveSiblingsAfter(g, last)
  if (right.length > 0) {
    expandIfCollapsed(g, last, ops)
    const keys = keysAtEnd(g, last, right.length, rand)
    right.forEach((id, i) => ops.push(op.move(id, last, keys[i]!)))
  }
  for (const run of groups) {
    const parentId = g.must(run[0]!).parentId!
    const grand = g.must(parentId).parentId!
    const keys = keysAfter(g, grand, parentId, run.length, rand)
    run.forEach((id, i) => ops.push(op.move(id, grand, keys[i]!)))
  }
  return { ops }
}

// ------------------------------------------------------------------ ⌥↑ / ⌥↓

/**
 * ⌥↑. Swap with the sibling above. A first child moves to the end of its parent's previous
 * sibling (same depth, Logseq). Selections spanning several parents do not move.
 */
export function moveUp(ctx: Ctx, ids: readonly NodeId[]): Result {
  const { g, root, rand } = ctx
  const { groups } = selectionGroups(g, root, ids)
  if (groups.length !== 1) return NOOP
  const run = groups[0]!
  const parentId = g.must(run[0]!).parentId!
  const ops: OpBody[] = []
  const above = prevLiveSibling(g, run[0]!)
  if (above !== null) {
    const keys = keysBefore(g, parentId, above, run.length, rand)
    run.forEach((id, i) => ops.push(op.move(id, parentId, keys[i]!)))
    return { ops }
  }
  if (parentId === root) return NOOP
  const uncle = prevLiveSibling(g, parentId)
  if (uncle === null) return NOOP
  expandIfCollapsed(g, uncle, ops)
  const keys = keysAtEnd(g, uncle, run.length, rand)
  run.forEach((id, i) => ops.push(op.move(id, uncle, keys[i]!)))
  return { ops }
}

/** ⌥↓. Mirror of moveUp: a last child moves to the start of its parent's next sibling. */
export function moveDown(ctx: Ctx, ids: readonly NodeId[]): Result {
  const { g, root, rand } = ctx
  const { groups } = selectionGroups(g, root, ids)
  if (groups.length !== 1) return NOOP
  const run = groups[0]!
  const parentId = g.must(run[0]!).parentId!
  const ops: OpBody[] = []
  const below = nextLiveSibling(g, run[run.length - 1]!)
  if (below !== null) {
    const keys = keysAfter(g, parentId, below, run.length, rand)
    run.forEach((id, i) => ops.push(op.move(id, parentId, keys[i]!)))
    return { ops }
  }
  if (parentId === root) return NOOP
  const uncle = nextLiveSibling(g, parentId)
  if (uncle === null) return NOOP
  expandIfCollapsed(g, uncle, ops)
  const keys = keysAtStart(g, uncle, run.length, rand)
  run.forEach((id, i) => ops.push(op.move(id, uncle, keys[i]!)))
  return { ops }
}

// ------------------------------------------------------------------ delete, duplicate, collapse

/** Delete the selected roots with their subtrees. Caret to the block above, else below, else none. */
export function remove(ctx: Ctx, ids: readonly NodeId[]): Result {
  const { g, root } = ctx
  const { roots } = selectionGroups(g, root, ids)
  if (roots.length === 0) return NOOP
  const first = roots[0]!
  const lastRoot = roots[roots.length - 1]!
  const above = prevVisible(g, root, first)
  let caret: Caret | null = null
  if (above !== null) caret = at(above, g.must(above).content.length)
  else {
    // First block below the deleted range that is not inside it.
    let below = nextVisible(g, root, lastVisibleDescendant(g, lastRoot))
    while (below !== null && roots.some((r) => r === below || isAncestor(g, r, below!))) {
      below = nextVisible(g, root, below)
    }
    if (below !== null) caret = at(below, 0)
  }
  return { ops: roots.map((id) => op.delete(id)), caret }
}

/** ⌘D. Deep-copy one run of sibling roots right below it; selects the copies. */
export function duplicate(ctx: Ctx, ids: readonly NodeId[]): Result {
  const { g, root, rand } = ctx
  const { groups } = selectionGroups(g, root, ids)
  if (groups.length !== 1) return NOOP
  const run = groups[0]!
  const parentId = g.must(run[0]!).parentId!
  const keys = keysAfter(g, parentId, run[run.length - 1]!, run.length, rand)
  const ops: OpBody[] = []
  const copies = run.map((id, i) => copySubtree(ctx, id, parentId, keys[i]!, ops))
  return { ops, select: copies }
}

function copySubtree(ctx: Ctx, id: NodeId, parentId: NodeId, order: string, ops: OpBody[]): NodeId {
  const src = ctx.g.must(id)
  const n = newBlock(ctx, parentId, order, src.content, src)
  ops.push(op.create(n))
  const kids = liveChildren(ctx.g, id)
  const keys = keysBetween(null, null, kids.length, ctx.rand)
  kids.forEach((k, i) => copySubtree(ctx, k, n.id, keys[i]!, ops))
  return n.id
}

/** ⌘↑ / ⌘↓ on the block or selection. Only blocks with children change. */
export function setCollapsed(ctx: Ctx, ids: readonly NodeId[], collapsed: boolean): Result {
  const { g, root } = ctx
  const { roots } = selectionGroups(g, root, ids)
  const ops: OpBody[] = []
  for (const id of roots) {
    if (g.must(id).collapsed !== collapsed && hasLiveChildren(g, id)) ops.push(op.collapse(id, collapsed))
  }
  return { ops }
}

// ------------------------------------------------------------------ zoom (view only, no ops)

export function zoomIn(ctx: Ctx, id: NodeId): Result {
  if (!isBlockInView(ctx, id)) return NOOP
  return { ops: [], root: id }
}

export function zoomOut(ctx: Ctx): Result {
  const r = ctx.g.must(ctx.root)
  if (r.kind !== 'block' || r.parentId === null) return NOOP
  return { ops: [], root: r.parentId, caret: at(r.id, r.content.length) }
}

// ------------------------------------------------------------------ paste, drop

export interface OutlineNode {
  readonly content: string
  readonly children: readonly OutlineNode[]
}

/**
 * Paste a Markdown outline. Into an empty block: the block takes the first pasted root's text and
 * children; the other roots follow as siblings. Into a non-empty block: all roots follow it as
 * siblings. Caret at the end of the last pasted line.
 */
export function pasteTree(ctx: Ctx, input: TextInput, tree: readonly OutlineNode[]): Result {
  const { g, rand } = ctx
  if (tree.length === 0 || !isBlockInView(ctx, input.id)) return NOOP
  const b = g.must(input.id)
  const parentId = b.parentId!
  const ops: OpBody[] = []
  let lastId: NodeId = b.id
  let lastLen = 0
  const build = (node: OutlineNode, parent: NodeId, order: string): void => {
    const n = newBlock(ctx, parent, order, node.content)
    ops.push(op.create(n))
    lastId = n.id
    lastLen = node.content.length
    const keys = keysBetween(null, null, node.children.length, rand)
    node.children.forEach((c, i) => build(c, n.id, keys[i]!))
  }
  let rest = tree
  if (input.text.trim() === '') {
    const first = tree[0]!
    if (b.content !== first.content) ops.push(op.content(b.id, first.content))
    lastId = b.id
    lastLen = first.content.length
    const keys = keysAtStart(g, b.id, first.children.length, rand)
    first.children.forEach((c, i) => build(c, b.id, keys[i]!))
    rest = tree.slice(1)
  }
  const keys = keysAfter(g, parentId, b.id, rest.length, rand)
  rest.forEach((r, i) => build(r, parentId, keys[i]!))
  return { ops, caret: at(lastId, lastLen) }
}

export type DropWhere = 'before' | 'after' | 'child'

/**
 * Drag and drop: move the selected roots (with subtrees) before/after a target block or as its
 * first children. The target may be in another view or page (the explicit cross-page move).
 * Roots from several parents land as one run of siblings. Refuses drops into the moved subtrees.
 */
export function dropAt(ctx: Ctx, ids: readonly NodeId[], target: NodeId, where: DropWhere): Result {
  const { g, rand } = ctx
  const { roots } = selectionGroups(g, ctx.root, ids)
  if (roots.length === 0 || !g.isLive(target)) return NOOP
  for (const r of roots) if (r === target || isAncestor(g, r, target)) return NOOP
  const t = g.must(target)
  const asChild = where === 'child' || t.parentId === null
  const ops: OpBody[] = []
  let parentId: NodeId
  let keys: string[]
  if (asChild) {
    parentId = target
    expandIfCollapsed(g, target, ops)
    keys = keysAtStart(g, target, roots.length, rand)
  } else {
    parentId = t.parentId!
    keys =
      where === 'before'
        ? keysBefore(g, parentId, target, roots.length, rand)
        : keysAfter(g, parentId, target, roots.length, rand)
  }
  roots.forEach((id, i) => ops.push(op.move(id, parentId, keys[i]!)))
  return { ops, select: roots }
}
